import {
  LambdaClient,
  InvokeCommand,
  waitUntilFunctionActiveV2,
} from "@aws-sdk/client-lambda";
import React from "react";
import { Auth, Storage } from "aws-amplify";
import { v4 as uuid } from "uuid";
import config from "aws-exports";
import * as XLSX from "xlsx";
import { getParameterFromManager } from "./Utils";
import { userTracer } from "../logging";
import { createUiLogger } from "../logging/uiLogger";
import { prepareBrowserLambdaInvokeInput } from "./lambdaPayloadEnvelope";
import { isUserVaultUpdateSuccessful } from "../userVaultResponse";
const console = createUiLogger("meaplatform/services/Helper.js");


const getCurrentScreen = () =>
  typeof window !== "undefined" ? window.location.pathname : "/";


const wait = (ms) => new Promise((res) => setTimeout(res, ms));

const callWithRetry = async (fn, reqopt, fixedSleep = undefined, depth = 0) => {
  try {
    return await fn(reqopt);
  } catch (e) {
    if (depth > 9) {
      console.error(`callWithRetry: exhausted`);
      throw e;
    }
    if (fixedSleep === undefined) {
      await wait(2 ** depth * 300);
    } else {
      await wait(fixedSleep);
    }

    return callWithRetry(fn, reqopt, fixedSleep, depth + 1);
  }
};

export function deepJsonParse(obj) {
  if (typeof obj === "string") {
    try {
      obj = JSON.parse(obj);
    } catch (e) {
      return obj; // Return the original string if it's not JSON
    }
  }

  if (typeof obj === "object" && obj !== null) {
    for (let key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        obj[key] = deepJsonParse(obj[key]);
      }
    }
  }

  return obj;
}

  export function getTemplatePromptGenerationError(response) {
  const defaultError = "Template prompt generation failed.";
  const candidates = [];
  let current = response;

  for (let depth = 0; depth < 4 && current; depth++) {
    candidates.push(current);
    current = current?.response && current.response !== current ? current.response : null;
  }

  for (const candidate of [...candidates].reverse()) {
    const parsedCandidate = deepJsonParse(candidate);
    const parsedBody = parsedCandidate?.body !== undefined
      ? deepJsonParse(parsedCandidate.body)
      : undefined;
    const statusCode = Number(parsedCandidate?.statusCode ?? parsedBody?.statusCode);
    const error = parsedCandidate?.error || parsedBody?.error;
    const message = parsedCandidate?.message || parsedBody?.message || error;

    if ((Number.isFinite(statusCode) && statusCode >= 400) || error) {
      return {
        statusCode: Number.isFinite(statusCode) ? statusCode : 400,
        error: error || defaultError,
        message: message || defaultError
      };
    }
  }

  return null;
}

export async function invokeLambda(reqopt) {
  const credentials = await Auth.currentCredentials();
  const lambdaClient = new LambdaClient({
    credentials: Auth.essentialCredentials(credentials),
    region: config.aws_project_region,
  });
  const backend = config.aws_user_files_s3_bucket.split("-").at(-1);
  const guardedRequest = await prepareBrowserLambdaInvokeInput(reqopt, {
    bucketName: config.aws_user_files_s3_bucket,
    environment: backend,
    ownerId: credentials.identityId,
    storagePut: (key, body, options) => Storage.put(key, body, options),
  });
  const command = new InvokeCommand(guardedRequest);

  try {
    const data = await lambdaClient.send(command);
    const resp = guardedRequest.InvocationType
      ? data
      : deepJsonParse(JSON.parse(uint8Decode(data.Payload)));
    // console.log(`${reqopt.FunctionName} lambda response is ${JSON.stringify(resp)}`);// successful response
    if (resp?.error || resp?.errorMessage) {
      console.error(
        `Error in response from ${reqopt.FunctionName} lambda: ${JSON.stringify(
          resp
        )}`
      );
      throw new Error(
        `Error in response from ${reqopt.FunctionName} lambda: ${JSON.stringify(
          resp
        )}`
      );
    }
    return resp;
  } catch (error) {
    console.error(
      `Invoking ${reqopt.FunctionName} Lambda err: ${JSON.stringify(error)}`
    );
    if (error.name === "CodeArtifactUserPendingException") {
      console.debug(
        `${reqopt.FunctionName} is not in Active state, Waiting for a lambda to Active`
      );
      // wait for the function transit from Pending to Active
      const lambdaResponse = await waitUntilFunctionActiveV2(
        {
          client: lambdaClient,
          maxWaitTime: 300,
        },
        {
          FunctionName: reqopt.FunctionName,
        }
      );
      console.debug(
        `${reqopt.FunctionName} Lambda status is ${
          lambdaResponse?.reason?.Configuration?.State
        } now and calling ${
          reqopt.FunctionName
        } lambda again with params: ${JSON.stringify(reqopt)}`
      );
      return invokeLambda(reqopt);
    }
    throw error;
  }
}

export async function invokeLambdaWithRetry(
  functionName,
  payload,
  invocationType = undefined,
  fixedSleep = undefined,
  skipRouteResolution = false
) {
  const tracedTarget =
    typeof payload?.body === "string" ? deepJsonParse(payload.body)?.target : payload?.body?.target;
  const trace = userTracer.startOperation("lambda.invoke", {
    module: "services",
    screen: typeof window !== "undefined" ? window.location.pathname : "/",
    impactKind: "user-visible",
    impactReason: "Lambda request initiated from the active browser flow.",
    data: {
      functionName,
      target: tracedTarget || null,
      invocationType: invocationType || "RequestResponse",
    },
  });
  try {
  const asyncPollingTargets = new Set(["fetch-mapped-data", "finish"]);

  if (!skipRouteResolution || import.meta.env.VITE_DEFAULT_INVOCATION === "api") {
    // Get route config from parameter store
    let instanceLambdaTargetRoutes = await getParameterFromManager("INSTANCE_LAMBDA_TARGET_ROUTES");
    const reqData = typeof payload?.body === "string" ? deepJsonParse(payload) : payload;
    let invokeTypeForTarget = "lambda";
    invokeTypeForTarget = import.meta.env.VITE_DEFAULT_INVOCATION === "api" ||  instanceLambdaTargetRoutes?.default_invocation?.trim()?.toLowerCase() === "api" ? "api" : "lambda";
    if (instanceLambdaTargetRoutes && reqData?.body?.target) {
      const routeConfig = instanceLambdaTargetRoutes[reqData?.body?.target];
      if (routeConfig) {
        functionName = routeConfig?.handler?.trim();
        invokeTypeForTarget = instanceLambdaTargetRoutes?.default_invocation ? instanceLambdaTargetRoutes?.default_invocation.trim()?.toLowerCase() : routeConfig?.invoke?.trim()?.toLowerCase() ?? "lambda";
      }
    }

    // If the target requires API invocation
    if (
      ["instanceHandler", "instanceHandlerLean", "instanceHandlerLight", "instanceHandlerLarge"].includes(functionName) &&
      invokeTypeForTarget === "api"
    ) {
      const result = await invokeLambdaWithAPI(functionName, payload, "POST");
      trace.finish({
        data: {
          functionName,
          target: tracedTarget || null,
          invocationType: "api",
        },
      });
      return result;
    }
  }

  // Otherwise, invoke directly through Lambda
  const shouldUseAsyncPollingFlow = asyncPollingTargets.has(payload?.body?.target);
  if (shouldUseAsyncPollingFlow && payload?.body && typeof payload.body === "object") {
    payload.body.asyncResponse = true;
  }
  const backend = config.aws_user_files_s3_bucket.split("-");
  const reqopt = {
    FunctionName: `${functionName}-${backend[backend.length - 1]}`,
    Payload: JSON.stringify(payload),
    ...((invocationType || shouldUseAsyncPollingFlow) ? { InvocationType: invocationType || "Event" } : {}),
  };

  const result = await callWithRetry(invokeLambda, reqopt, fixedSleep);
  if (shouldUseAsyncPollingFlow) {
    trace.finish({
      data: {
        functionName: reqopt.FunctionName,
        target: tracedTarget || null,
        invocationType: reqopt.InvocationType || "Event",
        asyncAccepted: true,
      },
    });
    return {
      response: {
        statusCode: 202,
        apiStatus: "Pending",
        message: "Request accepted and is being processed in the background."
      }
    };
  }
  trace.finish({
    data: {
      functionName: reqopt.FunctionName,
      target: tracedTarget || null,
      invocationType: invocationType || "RequestResponse",
    },
  });
  return result;
  } catch (error) {
    trace.fail(error, {
      data: {
        functionName,
        target: tracedTarget || null,
        invocationType: invocationType || "RequestResponse",
      },
    });
    throw error;
  }
}

export async function invokeLambdaWithAPI(
  functionName,
  payload,
  method = "POST",
  skipRouteResolution = false
) {
  const tracedTarget =
    typeof payload?.body === "string" ? deepJsonParse(payload.body)?.target : payload?.body?.target;
  const trace = userTracer.startOperation("api.invoke", {
    module: "services",
    screen: typeof window !== "undefined" ? window.location.pathname : "/",
    impactKind: "user-visible",
    impactReason: "API request initiated from the active browser flow.",
    data: {
      functionName,
      target: tracedTarget || null,
      method,
    },
  });
  const nameMap = {
    instanceHandler: "nm",
    instanceHandlerLean: "ln",
    instanceHandlerLight: "lh",
    instanceHandlerLarge: "xl"
  };
  
  const body = typeof payload === 'string' ? JSON.parse(payload) : payload;
  if (!skipRouteResolution && ["instanceHandler", "instanceHandlerLean", "instanceHandlerLight", "instanceHandlerLarge"].includes(functionName)) {
    const instanceLambdaTargetRoutes = await getParameterFromManager('INSTANCE_LAMBDA_TARGET_ROUTES');
    if (instanceLambdaTargetRoutes) {
      const routeConfig = instanceLambdaTargetRoutes[payload?.body?.target];
      if (routeConfig) {
        functionName = routeConfig?.handler?.trim();
      }
    }
  }

  const backend = config.aws_user_files_s3_bucket.split("-");
  const targetLM = nameMap[functionName] ?? 'nm';
  const apiBase = `https://instancehandler-${backend[backend.length - 1]}.meaplatform.com/`;
  const url = `${apiBase}${targetLM}/v1/invoke`;
  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(body),
    });
    let resp = await response.json();
    resp = deepJsonParse(resp);
    trace.finish({
      data: {
        functionName,
        target: tracedTarget || null,
        method,
        status: response.status,
      },
    });
    return resp;
  } catch (err) {
    trace.fail(err, {
      data: {
        functionName,
        target: tracedTarget || null,
        method,
      },
    });
    console.error("API Gateway call failed", err);
    throw err;
  }
}

export function getDynamicTVName(tabOrViewName, nostrip) {
  var region = config.aws_project_region.replace(/-/g, "_");
  var backend = config.aws_user_files_s3_bucket.split("-");
  var backendenv = backend[backend.length - 1];
  if (nostrip)
    return `meadatastore_${region.replace(
      /-/g,
      "_"
    )}_${backendenv}.${tabOrViewName}`;
  return `meadatastore_${region.replace(
    /-/g,
    "_"
  )}_${backendenv}.${tabOrViewName.replace(/fqso/g, "")}`;
}

export async function signOutUser() {
  const currentUser = Auth.userPool.getCurrentUser();
  let userAuth = await Auth.currentAuthenticatedUser({
    bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
  });
  var body = {
    access_token: userAuth.signInUserSession.idToken.jwtToken,
    target: "logout"
  };
  const init = {
    body: body,
  };
  await invokeLambdaWithRetry("instanceHandler", init, "Event");
  if (currentUser !== null) {
    currentUser.getSession((err) => {
      if (err) {
        console.error("Error getting session:", err);
        return;
      }
      currentUser.globalSignOut({
        onSuccess: (result) => {
          console.error("Global sign-out successful:", result);
        },
        onFailure: (err) => {
          console.error("Global sign-out failed:", err);
        },
      });
    });
  }
  await Auth.signOut({ global: true });
}

function storagePathCandidates(filePath) {
  const rawPath = String(filePath || "").trim();
  if (!rawPath) {
    return [];
  }
  if (/^https?:\/\//i.test(rawPath)) {
    return [rawPath];
  }
  let normalizedPath = rawPath.replace(/^\/+/, "");
  if (/^s3:\/\//i.test(normalizedPath)) {
    const [, ...pathParts] = normalizedPath.replace(/^s3:\/\//i, "").split("/");
    normalizedPath = pathParts.join("/");
  }
  const withoutPublicPrefix = normalizedPath.replace(/^public\//, "");
  return Array.from(new Set([withoutPublicPrefix, normalizedPath].filter(Boolean)));
}

export async function storageResultToBlob(body) {
  if (!body) {
    throw new Error("S3 download returned no body");
  }
  if (body instanceof Blob) {
    return body;
  }
  if (body instanceof ArrayBuffer) {
    return new Blob([body]);
  }
  if (ArrayBuffer.isView(body)) {
    return new Blob([body.buffer.slice(body.byteOffset, body.byteOffset + body.byteLength)]);
  }
  if (typeof body === "string") {
    if (/^https?:\/\//i.test(body)) {
      const response = await fetch(body);
      if (!response.ok) {
        throw new Error(`S3 download failed with HTTP ${response.status}`);
      }
      return response.blob();
    }
    return new Blob([body]);
  }
  if (typeof body.blob === "function") {
    return body.blob();
  }
  if (typeof body.arrayBuffer === "function") {
    return new Blob([await body.arrayBuffer()]);
  }
  if (typeof body.transformToByteArray === "function") {
    return new Blob([await body.transformToByteArray()]);
  }
  if (body.Body || body.body) {
    return storageResultToBlob(body.Body || body.body);
  }
  throw new Error(`Unsupported S3 download body type: ${Object.prototype.toString.call(body)}`);
}

export async function downloadS3Object(filePath, filename, options = {}) {
  const candidates = storagePathCandidates(filePath);
  if (candidates.length === 0) {
    throw new Error("Cannot download S3 object without a file path");
  }

  let lastError;
  for (const candidate of candidates) {
    try {
      if (/^https?:\/\//i.test(candidate)) {
        const response = await fetch(candidate);
        if (!response.ok) {
          throw new Error(`S3 download failed with HTTP ${response.status}`);
        }
        return downloadBlob(await response.blob(), filename);
      }
      const result = await Storage.get(candidate, {
        download: true,
        expires: 60,
        cacheControl: "no-cache",
        ...options,
      });
      return downloadBlob(await storageResultToBlob(result?.Body || result?.body || result), filename);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error(`Unable to download S3 object: ${filePath}`);
}

export function downloadBlob(blob, filename) {
  if (!blob) {
    throw new Error("Cannot download an empty file body");
  }
  if (!(blob instanceof Blob)) {
    if (blob instanceof ArrayBuffer) {
      blob = new Blob([blob]);
    } else if (ArrayBuffer.isView(blob)) {
      blob = new Blob([blob.buffer.slice(blob.byteOffset, blob.byteOffset + blob.byteLength)]);
    } else if (typeof blob === "string") {
      blob = new Blob([blob]);
    } else if (blob.Body instanceof Blob) {
      blob = blob.Body;
    } else if (blob.body instanceof Blob) {
      blob = blob.body;
    } else {
      throw new Error(`Cannot download unsupported file body type: ${Object.prototype.toString.call(blob)}`);
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener("click", clickHandler);
    }, 150);
  };
  a.addEventListener("click", clickHandler, false);
  a.click();
  return a;
}

export async function downloadExcel(buffer, fileName) {
  const newWorkbook = XLSX.utils.book_new();
  const workbook = XLSX.read(buffer, { type: "array", cellNF: true });
  for (let i = 0; i < workbook.SheetNames.length; i++) {
    let sheetName = workbook.SheetNames[i];
    let originalSheet = workbook.Sheets[sheetName];

    if (sheetName === "Combined Sheet") {
      XLSX.utils.book_append_sheet(
        newWorkbook,
        originalSheet,
        "Combined Sheet"
      );
    } else {
      XLSX.utils.book_append_sheet(newWorkbook, originalSheet, sheetName);
    }
  }
  XLSX.writeFile(newWorkbook, fileName, { compression: true });
}

export async function loadS3File(jsonPath) {
  const trace = userTracer.startOperation("s3.load-json", {
    module: "services",
    screen: getCurrentScreen(),
    impactKind: "user-visible",
    impactReason: "Route or UI state is waiting on JSON loaded from S3.",
    data: {
      filePath: jsonPath,
      bucket: config.aws_user_files_s3_bucket,
      region: config.aws_project_region,
    },
  });
  try {
    const jsonS3DataRes = await Storage.get(jsonPath, { download: true });
    var json_data = await (await storageResultToBlob(jsonS3DataRes?.Body || jsonS3DataRes?.body || jsonS3DataRes)).text();
    var data = JSON.parse(json_data);
    trace.finish({
      data: {
        filePath: jsonPath,
        bucket: config.aws_user_files_s3_bucket,
        region: config.aws_project_region,
        found: true,
      },
    });
    return data;
  } catch (error) {
    trace.fail(error, {
      data: {
        filePath: jsonPath,
        bucket: config.aws_user_files_s3_bucket,
        region: config.aws_project_region,
      },
    });
    throw error;
  }
}

export function isEmailChar(name) {
  var format = /[!#$%^&*()_+\-=[\]{};':"\\|,<>/?]+/;
  if (format.test(name)) {
    return false;
  }
  return true;
}

export async function fetchMsg(subID) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    const body = {
      access_token: user.signInUserSession.idToken.jwtToken,
      target: "fetchMsg",
      submissionIdentifier: subID,
    };
    const init = {
      body: body,
    };
    const resp = await invokeLambdaWithRetry("instanceHandler", init);
    return resp.response;
  } catch (err) {
    console.error(err);
    return false;
  }
}

export async function fetchEmailPreview(documentFullPath, documentName = "") {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true,
    });
    const body = {
      access_token: user.signInUserSession.idToken.jwtToken,
      target: "fetchEmailPreview",
      documentFullPath,
      documentName,
    };
    const init = {
      body: body,
    };
    const resp = await invokeLambdaWithRetry("instanceHandler", init);
    return resp?.response || null;
  } catch (err) {
    console.error(err);
    return null;
  }
}

export async function fetchDocument(docID) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    const body = {
      access_token: user.signInUserSession.idToken.jwtToken,
      target: "fetchDocument",
      document_id: docID,
    };
    const init = {
      body: body,
    };
    const resp = await invokeLambdaWithRetry("instanceHandler", init);
    return resp.response;
  } catch (err) {
    console.error(err);
    return false;
  }
}

export function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export async function setUserVault(
  password,
  given_name,
  family_name,
  phone_number,
  company,
  country,
  groups,
  user_sub,
  extraInfo
) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    var user_type = "cog_int_user";
    if (user?.attributes?.identities !== undefined) {
      var identities = JSON.parse(user.attributes.identities);
      if (identities[0].providerType === "OIDC") {
        user_type = "ext_user";
      }
    }
    const body = {
      "target": "sysUserManager",
      access_token: user.signInUserSession.idToken.jwtToken,
      option: "register-profile",
      passcode: password,
      given_name: given_name,
      family_name: family_name,
      phone_number: phone_number,
      company: company,
      country: country,
      groups: groups,
      user_type: user_type,
      user_sub: user_sub,
      Active: 1,
      extraInfo: extraInfo,
    };
    const init = {
      body: JSON.stringify(body),
    };
    const result = await invokeLambdaWithRetry("instanceHandlerLean", init);
    if (!isUserVaultUpdateSuccessful(result)) {
      throw new Error("UserVault update was not acknowledged");
    }
  } catch (err) {
    console.error("UserVault update failed");
    return false;
  }
  return true;
}

export function renderReactFragment(showModalYesNoMsg) {
  var showModalYesNoMsgParts = showModalYesNoMsg.split("<b>");
  var msgParts = [];
  for (var i = 0; i < showModalYesNoMsgParts.length; i++) {
    var partParts = showModalYesNoMsgParts[i].split("</b>");
    if (partParts.length === 1) {
      msgParts.push({ bold: false, msg: partParts[0] });
    } else {
      msgParts.push({ bold: true, msg: partParts[0] });
      msgParts.push({ bold: false, msg: partParts[1] });
    }
  }
  return (
    <>
      {msgParts.map((item, key) => (
        <React.Fragment key={key}>
          {!item.bold && item.msg}
          {item.bold && <b>{item.msg}</b>}
        </React.Fragment>
      ))}
    </>
  );
}

export async function isExistingTemplate(srchTemplate) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    const body = {
      "target": "fetchTemplateList",
      access_token: user.signInUserSession.idToken.jwtToken,
      count: -1,
      rowStart: 0,
      pageSize: 2,
      searchTemplateName: srchTemplate,
      mode: "exact-no-data",
    };
    const init = {
      body: JSON.stringify(body),
    };
    const data = await invokeLambdaWithRetry("instanceHandlerLight", init);
    return data.count >= 1;
  } catch (err) {
    console.error("err: ", err);
    return false;
  }
}

export async function isExistingLOBCode(srchLOBCode) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    const body = {
      "target": "fetchLOBCodeList",
      access_token: user.signInUserSession.idToken.jwtToken,
      count: -1,
      rowStart: 0,
      pageSize: 2,
      searchLOBCode: srchLOBCode,
      mode: "exact-no-data",
    };
    const init = {
      body: JSON.stringify(body),
    };
    const data = await invokeLambdaWithRetry("instanceHandlerLean", init);
    return data.count === 1;
  } catch (err) {
    console.error("err: ", err);
    return false;
  }
}

export async function processUpload(
  submissionIdentifier,
  templateName,
  inputFiles,
  selectedGroup
) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    const body = {
      "target": "extractDocument",
      access_token: user.signInUserSession.idToken.jwtToken,
      submissionIdentifier: submissionIdentifier,
      templateName: templateName[0],
      inputFiles: inputFiles,
      option: "process_upload",
      group_id: selectedGroup,
      source: "UI",
      templateList: templateName,
    };
    const init = {
      body: JSON.stringify(body),
    };
    const resp = await invokeLambdaWithRetry("instanceHandler", init);
    return resp;
  } catch (err) {
    console.error("err: ", err);
    return false;
  }
}

export function isTemplateAllowed(documentName) {
  if (documentName.toLowerCase().endsWith(".zip")) return true;
  if (documentName.toLowerCase().endsWith(".msg")) return true;
  if (documentName.toLowerCase().endsWith(".eml")) return true;
  if (documentName.toLowerCase().endsWith(".pdf")) return true;
  if (documentName.toLowerCase().endsWith(".docx")) return true;
  if (documentName.toLowerCase().endsWith(".pptx")) return true;
  if (documentName.toLowerCase().endsWith(".xlsx")) return true;
  if (documentName.toLowerCase().endsWith(".doc")) return true;
  if (documentName.toLowerCase().endsWith(".ppt")) return true;
  if (documentName.toLowerCase().endsWith(".xls")) return true;
  if (documentName.toLowerCase().endsWith(".jpg")) return true;
  if (documentName.toLowerCase().endsWith(".jpeg")) return true;
  if (documentName.toLowerCase().endsWith(".png")) return true;
  if (documentName.toLowerCase().endsWith(".mp4")) return true;
  return false;
}

export async function cptood(
  submissionIdentifier,
  filePath,
  fileName,
  movefolder
) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    const body = {
      "target": "cponedrive",
      filePath: filePath !== undefined ? `public/${filePath}` : undefined,
      access_token: user.signInUserSession.idToken.jwtToken,
      SubmissionId: submissionIdentifier,
      fileName: fileName,
      correlid: uuid(),
      option:
        movefolder === true
          ? filePath === undefined
            ? ["move-folder"]
            : ["copy-file", "move-folder"]
          : ["copy-file"],
    };
    const init = {
      body: JSON.stringify(body),
    };

    const resp = await invokeLambdaWithRetry("instanceHandler", init);
    console.info("cptood :", resp); // successful response
  } catch (err) {
    console.error("err: ", err);
    return false;
  }
  return true;
}

export async function extractDocument(
  request,
  option,
  altPath,
  docName,
  retry,
  msgDoc,
  reprocTemplateName,
  group_id
) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });

    var body = {};

    if (
      option === "newupload" ||
      option === "reproc-template" ||
      option === "reproc-template-simple"
    ) {
      body = {
        target: "extractDocument",
        document_id: request.document_id,
        access_token: user.signInUserSession.idToken.jwtToken,
        documentFullPath: request.documentFullPath,
        documentName: request.documentName,
        submissionIdentifier: request.submissionIdentifier,
        username: user.username,
        option: option,
        origin_document_id: request.origin_document_id,
        group_id: group_id,
      };
      if (msgDoc === true) {
        body.msgExploded = true;
      }
    } else if (option === "edited") {
      body = {
        target: "extractDocument",
        document_id: request.document_id,
        access_token: user.signInUserSession.idToken.jwtToken,
        documentFullPath: altPath,
        documentName: docName,
        submissionIdentifier: request.submissionIdentifier,
        option: option,
      };
    }

    if (option === "reproc-template" || option === "reproc-template-simple") {
      body.templateName = reprocTemplateName;
      body.isMerged = request.isMerged;
    }

    if (retry === true) {
      body.retry = true;
      body.templateName = reprocTemplateName;
    }

    const init = {
      body: JSON.stringify(body),
    };
    await invokeLambdaWithRetry("instanceHandler", init);
  } catch (err) {
    console.error("err: ", err);
    return false;
  }
  return true;
}

export async function notifyUser(request) {
  try {
    var body = {};
    if (request.option === "newDocumentCreated") {
      body = {
        "target": "notifyUserDocStage",
        toAddress: request.toAddress,
        name: request.name,
        fileName: request.fileName,
        option: request.option,
        submissionIdentifier: request.submissionIdentifier,
      };
    } else if (
      request.option === "documentComplete" ||
      request.option === "documentRejected" ||
      request.option === "documentInProgress" ||
      request.option === "documentDeleted" ||
      request.option === "qaInProgress" ||
      request.option === "qaComplete" ||
      request.option === "qaUploaded"
    ) {
      body = {
        "target": "notifyUserDocStage",
        username: request.username,
        processorname: request.processorname,
        fileName: request.fileName,
        option: request.option,
        submissionIdentifier: request.submissionIdentifier,
        rejectionReason: request?.rejectionReason,
        metaInfo: request?.metaInfo,
      };
    }
    const init = {
      body: JSON.stringify(body),
    };
    await invokeLambdaWithRetry("instanceHandlerLight", init);
  } catch (err) {
    console.error("err: ", err);
  }
}

var disableUploadVal = undefined;
var disableUploadValTs;
function isCacheValid(cacheValTs) {
  if (cacheValTs === undefined) {
    return false;
  }
  const now = new Date();
  const msBetweenDates = Math.abs(cacheValTs.getTime() - now.getTime());
  const secBetweenDates = msBetweenDates / 1000;
  if (secBetweenDates > 300) {
    return false;
  }
  return true;
}

export async function checkUploadStatus() {
  try {
    if (
      disableUploadVal === undefined ||
      isCacheValid(disableUploadValTs) === false
    ) {
      const user = await Auth.currentAuthenticatedUser({
        bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
      });
      const body = {
        access_token: user.signInUserSession.idToken.jwtToken,
        target: "checkUploadStatus",
      };
      const init = {
        body: body,
      };
      const result = await invokeLambdaWithRetry("instanceHandler", init);
      disableUploadValTs = new Date();
      disableUploadVal =
        result.response?.data?.getConfigValue?.configval === "true";
      return disableUploadVal;
    } else {
      return disableUploadVal;
    }
  } catch (err) {
    console.error(err);
    return false;
  }
}

export async function logAudit(
  document_id,
  submissionIdentifier,
  category,
  txtype,
  source,
  data1,
  data2,
  templateName,
  additionalInfo
) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });
    var body = {
      "target": "auditor",
      document_id: document_id,
      submissionIdentifier: submissionIdentifier,
      Category: category,
      TxType: txtype,
      Source: source,
      Data1: data1,
      Data2: data2,
      templateName: templateName,
      additionalInfo: additionalInfo,
      access_token: user.signInUserSession.idToken.jwtToken,
    };
    const init = {
      body: JSON.stringify(body),
    };
    await invokeLambdaWithRetry("instanceHandlerLean", init, "Event");
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
}

export function uint8Decode(uint8Array) {
  const decoder = new TextDecoder("utf-8");
  return decoder.decode(uint8Array);
}

export function getMergedMeaValidationCheckRules(meaValidationRules, domain) {
  const commonRules =
    meaValidationRules?.defaultDomains?.includes(domain) ||
    domain === "default" ||
    domain === undefined
      ? meaValidationRules?.default
      : {};
  const domainRules = meaValidationRules?.[domain] || {};
  return { ...commonRules, ...domainRules };
}

const EXTRACTED_WORKBOOK_MANIFEST_FORMAT = "mea.extractedWorkbookManifest";

function isExtractedWorkbookManifest(value) {
  return value?.format === EXTRACTED_WORKBOOK_MANIFEST_FORMAT && value?.storageMode === "chunked" && Array.isArray(value?.sheets);
}

async function hydrateExtractedWorkbookManifest(manifest) {
  const workbook = [];
  for (const sheetInfo of manifest.sheets) {
    const sheet = [];
    const chunks = Array.isArray(sheetInfo?.chunks) ? sheetInfo.chunks : [];
    for (const chunk of chunks) {
      if (!chunk?.path) continue;
      const chunkRows = await fetchJSONFromS3(chunk.path);
      if (!Array.isArray(chunkRows)) continue;
      if (sheet.length > 0 && sheetInfo?.headerIncludedInEachChunk === true && chunkRows.length > 0) {
        sheet.push(...chunkRows.slice(1));
      } else {
        sheet.push(...chunkRows);
      }
    }
    workbook[sheetInfo?.sheetIndex ?? workbook.length] = sheet;
  }
  return workbook;
}
export async function fetchJSONFromS3(filePath) {
  const trace = userTracer.startOperation("s3.fetch-json", {
    module: "services",
    screen: getCurrentScreen(),
    impactKind: "user-visible",
    impactReason: "Browser flow is waiting on JSON fetched from S3.",
    data: {
      filePath,
      bucket: config.aws_user_files_s3_bucket,
      region: config.aws_project_region,
    },
  });
  try {
    let resp = await Storage.get(filePath, {
      level: "public",
      bucket: config.aws_user_files_s3_bucket,
      region: config.aws_project_region,
      download: true,
      cacheControl: "no-cache",
    });

    // Convert Body to text based on the Amplify Storage result shape.
    const jsonData = await (await storageResultToBlob(resp?.Body || resp?.body || resp)).text();

    const parsed = JSON.parse(jsonData);
    const resolved = isExtractedWorkbookManifest(parsed) ? await hydrateExtractedWorkbookManifest(parsed) : parsed;
    trace.finish({
      data: {
        filePath,
        bucket: config.aws_user_files_s3_bucket,
        region: config.aws_project_region,
        found: true,
      },
    });
    return resolved;
  } catch (error) {
    if (error?.name === "NoSuchKey" || error?.code === "NoSuchKey") {
      trace.finish({
        data: {
          filePath,
          bucket: config.aws_user_files_s3_bucket,
          region: config.aws_project_region,
          found: false,
        },
      });
      return null; // File not found, return null silently
    }

    // Log only unexpected errors
    trace.fail(error, {
      data: {
        filePath,
        bucket: config.aws_user_files_s3_bucket,
        region: config.aws_project_region,
      },
    });
    console.error("Unexpected error fetching JSON from S3:", error);
    return null;
  }
}

export async function updateToS3(filePath, body) {
    const trace = userTracer.startOperation("s3.put-object", {
      module: "services",
      screen: getCurrentScreen(),
      impactKind: "user-blocking",
      impactReason: "User action is waiting on an S3 write to complete.",
      data: {
        filePath,
        bucket: config.aws_user_files_s3_bucket,
        region: config.aws_project_region,
      },
    });
    try {
      const result = await Storage.put(filePath, body, {
          level: 'public',
          bucket: config.aws_user_files_s3_bucket,
          region: config.aws_project_region
      });
      trace.finish({
        data: {
          filePath,
          bucket: config.aws_user_files_s3_bucket,
          region: config.aws_project_region,
        },
      });
      return result;
    } catch (error) {
      trace.fail(error, {
        data: {
          filePath,
          bucket: config.aws_user_files_s3_bucket,
          region: config.aws_project_region,
        },
      });
      throw error;
    }
}

export async function convertJsonToXl(jsonData) {
  // Convert JSON to an Excel worksheet
  const worksheet = XLSX.utils.json_to_sheet(jsonData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  // Write workbook to a buffer
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  // Convert buffer to a Blob
  const file = new Blob([excelBuffer]);
  return file;
}

export async function fetchLOBCodeList({
  count = -1,
  rowStart = 0,
  pageSize = 200,
  mode = "like",
} = {}) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: true,
    });

    const body = {
      target: "fetchLOBCodeList",
      access_token: user.signInUserSession.idToken.jwtToken,
      count,
      rowStart,
      pageSize,
      mode,
    };

    const data = await invokeLambdaWithRetry("instanceHandlerLean", {
      body: JSON.stringify(body),
    });
    return data?.lobCodes ?? [];
  } catch (err) {
    console.error("Failed to fetch LOB codes:", err);
    return false;
  }
}

export function vtlToJSON(vtlString) {
  if (typeof vtlString !== 'string') return [];

  const result = [];
  const itemRegex = /\{([^}]+)\}/g;
  let match;

  while ((match = itemRegex.exec(vtlString)) !== null) {
    const itemStr = match[1];
    const obj = {};

    // Only split at the first '=' to preserve commas in values
    const [key, ...rest] = itemStr.split('=');
    obj[key.trim()] = rest.join('=').trim();

    result.push(obj);
  }
  return result;
}
